

import {
  type ComponentProps,
  type ElementType,
  type ReactNode,
  cloneElement,
  isValidElement,
  useMemo,
} from 'react';

interface UseDisabledAnchorPropsInterface {
  /** Whether the element is disabled */
  disabled?: boolean;

  /** Whether the component uses asChild */
  asChild?: boolean;

  /** The children to check/modify */
  children?: ReactNode;

  /**
   * Usually, these will be the props returned from `useDisabledState`
   * that prevent all interactive events from being triggered.
   */
  a11yProps?: Partial<ComponentProps<ElementType>>;
}

/**
 * Hook that handles disabled anchor elements when using the asChild pattern.
 * Removes href attribute and applies accessibility properties to prevent navigation
 * while maintaining visual appearance. Essential for disabled links in component libraries.
 *
 * @param {object} props - Configuration object for disabled anchor handling.
 * @param {boolean} [props.disabled=false] - Whether the element should be disabled.
 * @param {boolean} [props.asChild=false] - Whether the component uses asChild pattern.
 * @param {ReactNode} [props.children=null] - The children to potentially modify.
 * @param {object} [props.a11yProps={}] - Additional accessibility properties to apply.
 * @returns {ReactNode} Modified children with disabled anchor props applied, or original children.
 */
export const useDisabledAnchor = ({
  asChild = false,
  children = null,
  disabled = false,
  a11yProps = {},
}: UseDisabledAnchorPropsInterface) =>
  useMemo(
    () =>
      asChild && disabled && isValidElement(children) && children.type === 'a'
        ? cloneElement(children, { href: undefined, role: 'link', ...a11yProps } as any)
        : children,
    [asChild, children, disabled, a11yProps],
  );
